import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  AlertTriangle,
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileCheck2,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  X,
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import {
  finalizeExtractionBatchReview,
  getExtractionBatchReview,
  saveExtractionBatchReviewCase,
  saveExtractionBatchReviewer,
  type BatchReviewCandidate,
  type BatchReviewCandidateState,
  type BatchReviewCase,
  type BatchReviewEvent,
  type BatchReviewEventValues,
  type BatchReviewFinalizeResponse,
  type BatchReviewSummary,
} from '../services/adminApi';
import {
  Banner,
  ButtonRow,
  Card,
  CardDescription,
  CardTitle,
  ConfirmDialog,
  PrimaryButton,
  SecondaryButton,
} from '../components/admin/AdminUI';
import { borderRadius, breakpoints, colors, shadows, spacing, typography } from '../styles/theme';
import { toAbsoluteUrl } from '../utils/externalUrl';

const MarkdownMessage = React.lazy(() =>
  import('../components/admin/agent/MarkdownMessage').then(module => ({
    default: module.MarkdownMessage,
  })),
);

const FIELD_LABELS: Array<[keyof BatchReviewEventValues, string]> = [
  ['title', 'Title'],
  ['startDate', 'Start date'],
  ['endDate', 'End date'],
  ['type', 'Type'],
  ['venue', 'Venue'],
  ['city', 'City'],
  ['state', 'State / province'],
  ['country', 'Country'],
  ['registrationUrl', 'Registration URL'],
  ['eventDetailUrl', 'Event detail URL'],
];

const PageHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 1fr) auto;
  gap: ${spacing[4]};
  align-items: end;
  margin-bottom: ${spacing[5]};

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ReviewerField = styled.label`
  display: grid;
  gap: ${spacing[1]};
  min-width: 280px;
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.secondary};
  text-transform: uppercase;
`;

const Input = styled.input`
  width: 100%;
  min-width: 0;
  min-height: 38px;
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  background: ${colors.white};
  color: ${colors.text.primary};
  font: inherit;

  &:focus {
    outline: 2px solid ${colors.primary[200]};
    border-color: ${colors.primary[500]};
  }
`;

const Select = styled.select`
  width: 100%;
  min-width: 0;
  min-height: 36px;
  padding: ${spacing[2]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  background: ${colors.white};
  color: ${colors.text.primary};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 72px;
  resize: vertical;
  padding: ${spacing[2]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  color: ${colors.text.primary};
  font: inherit;
`;

const ProgressTrack = styled.div`
  height: 10px;
  overflow: hidden;
  border-radius: ${borderRadius.full};
  background: ${colors.gray[200]};
`;

const ProgressFill = styled.div.withConfig({
  shouldForwardProp: prop => prop !== 'percentage',
})<{ percentage: number }>`
  width: ${props => Math.max(0, Math.min(100, props.percentage))}%;
  height: 100%;
  background: ${props => props.percentage === 100 ? colors.success[500] : colors.primary[500]};
  transition: width 0.2s ease;
`;

const Cases = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(205px, 1fr));
  gap: ${spacing[2]};
`;

const CaseButton = styled.button.withConfig({
  shouldForwardProp: prop => !['active', 'complete'].includes(prop),
})<{ active?: boolean; complete?: boolean }>`
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  gap: ${spacing[2]};
  align-items: center;
  min-height: 58px;
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${props => props.active ? colors.primary[500]
    : props.complete ? colors.success[300] : colors.border.normal};
  border-radius: ${borderRadius.md};
  background: ${props => props.active ? colors.primary[50]
    : props.complete ? colors.success[50] : colors.white};
  color: ${colors.text.primary};
  text-align: left;
  cursor: pointer;

  &:hover { border-color: ${colors.primary[400]}; }
`;

const CaseName = styled.div`
  min-width: 0;
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CaseProgress = styled.div`
  margin-top: 2px;
  color: ${colors.text.tertiary};
  font-size: ${typography.fontSizes.xs};
`;

const Workspace = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 0.9fr) minmax(500px, 1.3fr);
  gap: ${spacing[4]};
  min-width: 0;

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
  }
`;

const Pane = styled.section`
  min-width: 0;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  background: ${colors.white};
  box-shadow: ${shadows.sm};
  overflow: hidden;
`;

const PaneHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[3]};
  padding: ${spacing[3]} ${spacing[4]};
  border-bottom: 1px solid ${colors.border.light};
  background: ${colors.gray[50]};
`;

const PaneTitle = styled.h3`
  margin: 0;
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes.base};
`;

const Source = styled.div`
  max-height: 72vh;
  min-height: 560px;
  overflow: auto;
  padding: ${spacing[4]};

  @media (max-width: 1180px) {
    min-height: 360px;
    max-height: 55vh;
  }
`;

const ReviewBody = styled.div`
  max-height: 72vh;
  min-height: 560px;
  overflow: auto;

  @media (max-width: 1180px) {
    min-height: 440px;
    max-height: none;
  }
`;

const Segments = styled.div`
  display: inline-flex;
  padding: 3px;
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  background: ${colors.white};
`;

const Segment = styled.button.withConfig({
  shouldForwardProp: prop => prop !== 'active',
})<{ active?: boolean }>`
  padding: ${spacing[2]} ${spacing[3]};
  border: 0;
  border-radius: ${borderRadius.base};
  background: ${props => props.active ? colors.primary[600] : 'transparent'};
  color: ${props => props.active ? colors.white : colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
  cursor: pointer;
`;

const ReviewToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[2]};
  padding: ${spacing[3]} ${spacing[4]};
  border-bottom: 1px solid ${colors.border.light};
`;

const Toggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
`;

const ReviewRow = styled.article.withConfig({
  shouldForwardProp: prop => prop !== 'status',
})<{ status: string }>`
  padding: ${spacing[4]};
  border-bottom: 1px solid ${colors.border.light};
  border-left: 4px solid ${props => props.status === 'confirmed' ? colors.success[500]
    : props.status === 'rejected' ? colors.error[500] : colors.warning[400]};
  background: ${props => props.status === 'confirmed' ? colors.success[50]
    : props.status === 'rejected' ? colors.error[50] : colors.white};
`;

const ReviewHeadline = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[3]};
`;

const ReviewTitle = styled.h4`
  margin: 0;
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.primary};
`;

const Meta = styled.div`
  margin-top: ${spacing[1]};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.xs};
  overflow-wrap: anywhere;
`;

const Evidence = styled.blockquote`
  margin: ${spacing[3]} 0;
  padding: ${spacing[2]} ${spacing[3]};
  border-left: 3px solid ${colors.primary[300]};
  background: ${colors.primary[50]};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.xs};
  line-height: ${typography.lineHeights.relaxed};
`;

const SmallButton = styled.button.withConfig({
  shouldForwardProp: prop => prop !== 'tone',
})<{ tone?: 'success' | 'error' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  min-height: 32px;
  padding: ${spacing[1]} ${spacing[2]};
  border: 1px solid ${props => props.tone === 'success' ? colors.success[400]
    : props.tone === 'error' ? colors.error[400] : colors.border.normal};
  border-radius: ${borderRadius.md};
  background: ${colors.white};
  color: ${props => props.tone === 'success' ? colors.success[700]
    : props.tone === 'error' ? colors.error[700] : colors.text.primary};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.semibold};
  cursor: pointer;

  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${spacing[3]};
  margin-top: ${spacing[3]};

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const EditField = styled.label`
  display: grid;
  gap: 3px;
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.semibold};
`;

const Details = styled.details`
  margin-top: ${spacing[3]};

  summary {
    cursor: pointer;
    color: ${colors.primary[700]};
    font-size: ${typography.fontSizes.xs};
    font-weight: ${typography.fontWeights.semibold};
  }
`;

const Mapping = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${spacing[2]};
  align-items: end;
  margin-top: ${spacing[3]};

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const Empty = styled.div`
  padding: ${spacing[8]} ${spacing[4]};
  text-align: center;
  color: ${colors.text.tertiary};
`;

function percentage(reviewed: number, total: number): number {
  return total ? (reviewed / total) * 100 : 100;
}

function cloneEvents(events: BatchReviewEvent[]): BatchReviewEvent[] {
  return events.map(event => ({ ...event, values: { ...event.values } }));
}

function cloneCandidateReviews(candidates: BatchReviewCandidate[]): BatchReviewCandidateState[] {
  return candidates.map(candidate => ({ ...candidate.review }));
}

function candidateTitle(candidate: BatchReviewCandidate): string {
  return candidate.event.title || candidate.candidateId;
}

function eventLabel(event: BatchReviewEvent): string {
  return `${event.values.startDate || 'No date'} · ${event.values.title || 'Untitled event'}`;
}

function newEvent(): BatchReviewEvent {
  const id = typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return {
    goldEventId: `human-${id}`,
    origin: 'human_added',
    status: 'pending',
    values: Object.fromEntries(FIELD_LABELS.map(([field]) => [field, ''])) as unknown as BatchReviewEventValues,
    sourceEvidence: '',
    note: '',
  };
}

function SourcePane({ reviewCase }: { reviewCase: BatchReviewCase }) {
  const safeSource = toAbsoluteUrl(reviewCase.url);
  return (
    <Pane aria-label="Frozen source">
      <PaneHeader>
        <div>
          <PaneTitle>Frozen source</PaneTitle>
          <Meta>Captured Markdown used by both transports</Meta>
        </div>
        {safeSource && (
          <SmallButton as="a" href={safeSource} target="_blank" rel="noreferrer">
            <ExternalLink size={14} /> Live source
          </SmallButton>
        )}
      </PaneHeader>
      <Source>
        <Suspense fallback={<div>Rendering source…</div>}>
          <MarkdownMessage text={reviewCase.sourceMarkdown} />
        </Suspense>
      </Source>
    </Pane>
  );
}

interface EventReviewProps {
  event: BatchReviewEvent;
  onChange: (event: BatchReviewEvent) => void;
  onDelete: () => void;
}

function EventReview({ event, onChange, onDelete }: EventReviewProps) {
  const setStatus = (status: BatchReviewEvent['status']) => onChange({ ...event, status });
  const setField = (field: keyof BatchReviewEventValues, value: string) => onChange({
    ...event,
    status: 'pending',
    values: { ...event.values, [field]: value },
  });
  return (
    <ReviewRow status={event.status}>
      <ReviewHeadline>
        <div>
          <ReviewTitle>{event.values.title || 'Untitled source event'}</ReviewTitle>
          <Meta>{event.values.startDate || 'No start date'} → {event.values.endDate || 'No end date'} · {event.values.venue || 'No venue'}</Meta>
        </div>
        <ButtonRow>
          <SmallButton tone="success" onClick={() => setStatus('confirmed')}>
            <Check size={14} /> Confirm
          </SmallButton>
          <SmallButton tone="error" onClick={() => setStatus('rejected')}>
            <X size={14} /> Reject
          </SmallButton>
          {event.origin === 'human_added' && (
            <SmallButton tone="error" onClick={onDelete}>Remove</SmallButton>
          )}
        </ButtonRow>
      </ReviewHeadline>
      {event.sourceEvidence && <Evidence>{event.sourceEvidence}</Evidence>}
      <Details>
        <summary>Edit fields or add a review note</summary>
        <FieldGrid>
          {FIELD_LABELS.map(([field, label]) => (
            <EditField key={field}>{label}
              <Input value={event.values[field] || ''}
                onChange={change => setField(field, change.target.value)} />
            </EditField>
          ))}
        </FieldGrid>
        <EditField style={{ marginTop: spacing[3] }}>Source evidence
          <TextArea value={event.sourceEvidence}
            onChange={change => onChange({ ...event, sourceEvidence: change.target.value })} />
        </EditField>
        <EditField style={{ marginTop: spacing[3] }}>Reviewer note
          <TextArea value={event.note}
            onChange={change => onChange({ ...event, note: change.target.value })} />
        </EditField>
      </Details>
    </ReviewRow>
  );
}

interface CandidateReviewProps {
  candidate: BatchReviewCandidate;
  review: BatchReviewCandidateState;
  events: BatchReviewEvent[];
  onChange: (review: BatchReviewCandidateState) => void;
}

function CandidateReview({ candidate, review, events, onChange }: CandidateReviewProps) {
  const target = events.find(event => event.goldEventId === review.goldEventId);
  const proposed = candidate.decision === 'event'
    ? events.find(event => event.goldEventId === candidate.goldEventId)
    : null;
  const availableEvents = events.filter(event => event.status !== 'rejected');
  const confirmProposed = () => onChange({
    ...review,
    status: 'confirmed',
    decision: candidate.decision,
    goldEventId: candidate.decision === 'event' ? candidate.goldEventId : null,
  });
  return (
    <ReviewRow status={review.status}>
      <ReviewHeadline>
        <div>
          <ReviewTitle>{candidateTitle(candidate)}</ReviewTitle>
          <Meta>
            {candidate.event.startDate || 'No date'} · {candidate.event.venue || 'No venue'} ·
            observed {candidate.observationCount} time{candidate.observationCount === 1 ? '' : 's'}
          </Meta>
        </div>
        <ButtonRow>
          <SmallButton tone="success" disabled={candidate.decision === 'event' && !proposed}
            onClick={confirmProposed}>
            <Check size={14} /> Confirm proposed
          </SmallButton>
          <SmallButton tone="error" onClick={() => onChange({
            ...review, status: 'confirmed', decision: 'not_event', goldEventId: null,
          })}>
            <X size={14} /> Not an event
          </SmallButton>
        </ButtonRow>
      </ReviewHeadline>
      <Evidence>
        AI proposal: {candidate.decision === 'event' && proposed
          ? `map to “${eventLabel(proposed)}”`
          : 'not an event'}
      </Evidence>
      <Mapping>
        <EditField>Map prediction to source event
          <Select value={review.goldEventId || ''} onChange={change => onChange({
            ...review,
            status: 'pending',
            decision: 'event',
            goldEventId: change.target.value || null,
          })}>
            <option value="">Choose a source event…</option>
            {availableEvents.map(event => (
              <option key={event.goldEventId} value={event.goldEventId}>{eventLabel(event)}</option>
            ))}
          </Select>
        </EditField>
        <SmallButton tone="success" disabled={!review.goldEventId || target?.status === 'rejected'}
          onClick={() => onChange({ ...review, status: 'confirmed', decision: 'event' })}>
          <Check size={14} /> Confirm mapping
        </SmallButton>
      </Mapping>
      <Details>
        <summary>View all extracted fields and reviewer note</summary>
        <FieldGrid>
          {FIELD_LABELS.map(([field, label]) => (
            <div key={field}><Meta>{label}</Meta><div>{candidate.event[field] || '—'}</div></div>
          ))}
        </FieldGrid>
        <EditField style={{ marginTop: spacing[3] }}>Reviewer note
          <TextArea value={review.note}
            onChange={change => onChange({ ...review, note: change.target.value })} />
        </EditField>
      </Details>
    </ReviewRow>
  );
}

export default function AdminBatchVerificationPage() {
  const [summary, setSummary] = useState<BatchReviewSummary | null>(null);
  const [reviewCase, setReviewCase] = useState<BatchReviewCase | null>(null);
  const [events, setEvents] = useState<BatchReviewEvent[]>([]);
  const [candidateReviews, setCandidateReviews] = useState<BatchReviewCandidateState[]>([]);
  const [reviewer, setReviewer] = useState('');
  const [tab, setTab] = useState<'events' | 'predictions'>('events');
  const [pendingOnly, setPendingOnly] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalResult, setFinalResult] = useState<BatchReviewFinalizeResponse | null>(null);
  const requestIdRef = useRef(0);

  const applyCase = useCallback((value: BatchReviewCase) => {
    setReviewCase(value);
    setEvents(cloneEvents(value.events));
    setCandidateReviews(cloneCandidateReviews(value.candidates));
    setDirty(false);
    setTab('events');
    setPendingOnly(true);
  }, []);

  const loadCase = useCallback(async (caseId: string) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await getExtractionBatchReview(caseId);
      if (requestId !== requestIdRef.current || !response.case) return;
      setSummary(response.summary);
      setReviewer(response.summary.reviewer || '');
      applyCase(response.case);
    } catch (loadError: any) {
      if (requestId === requestIdRef.current) {
        setError(loadError?.message || 'Could not load the local Batch verification workspace');
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [applyCase]);

  useEffect(() => {
    let active = true;
    getExtractionBatchReview()
      .then(response => {
        if (!active) return;
        setSummary(response.summary);
        setReviewer(response.summary.reviewer || '');
        const first = response.summary.cases.find(item => !item.progress.complete)
          || response.summary.cases[0];
        if (first) loadCase(first.caseId);
        else setLoading(false);
      })
      .catch(loadError => {
        if (!active) return;
        setError(loadError?.message || 'This verification workspace is available only in local development.');
        setLoading(false);
      });
    return () => { active = false; };
  }, [loadCase]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const chooseCase = (caseId: string) => {
    if (reviewCase?.caseId === caseId) return;
    if (dirty && !window.confirm('Discard unsaved changes for this case?')) return;
    loadCase(caseId);
  };

  const updateEvent = (index: number, value: BatchReviewEvent) => {
    setEvents(current => current.map((event, eventIndex) => eventIndex === index ? value : event));
    setDirty(true);
  };

  const rejectEvent = (index: number, value: BatchReviewEvent) => {
    const rejectedId = value.goldEventId;
    updateEvent(index, value);
    setCandidateReviews(current => current.map(candidate =>
      candidate.goldEventId === rejectedId
        ? { ...candidate, status: 'pending', decision: 'not_event', goldEventId: null }
        : candidate));
  };

  const updateCandidate = (index: number, value: BatchReviewCandidateState) => {
    setCandidateReviews(current => current.map((candidate, candidateIndex) =>
      candidateIndex === index ? value : candidate));
    setDirty(true);
  };

  const localProgress = useMemo(() => {
    const eventsReviewed = events.filter(event => event.status !== 'pending').length;
    const candidatesReviewed = candidateReviews.filter(candidate => candidate.status === 'confirmed').length;
    return {
      eventsReviewed,
      candidatesReviewed,
      reviewed: eventsReviewed + candidatesReviewed,
      total: events.length + candidateReviews.length,
      complete: eventsReviewed === events.length && candidatesReviewed === candidateReviews.length,
    };
  }, [candidateReviews, events]);

  const saveCurrent = async (goNext = false) => {
    if (!reviewCase) return;
    setSaving(true);
    setError(null);
    try {
      const response = await saveExtractionBatchReviewCase({
        caseId: reviewCase.caseId,
        reviewer,
        events,
        candidates: candidateReviews,
      });
      setSummary(response.summary);
      if (response.case) applyCase(response.case);
      if (goNext) {
        const currentIndex = response.summary.cases.findIndex(item => item.caseId === reviewCase.caseId);
        const ordered = [
          ...response.summary.cases.slice(currentIndex + 1),
          ...response.summary.cases.slice(0, currentIndex),
        ];
        const next = ordered.find(item => !item.progress.complete);
        if (next) await loadCase(next.caseId);
      }
    } catch (saveError: any) {
      setError(saveError?.message || 'Could not save this review case');
    } finally {
      setSaving(false);
    }
  };

  const saveReviewer = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await saveExtractionBatchReviewer(reviewer);
      setSummary(response.summary);
    } catch (saveError: any) {
      setError(saveError?.message || 'Could not save reviewer name');
    } finally {
      setSaving(false);
    }
  };

  const confirmAllEvents = () => {
    setEvents(current => current.map(event => event.status === 'pending'
      ? { ...event, status: 'confirmed' } : event));
    setDirty(true);
  };

  const confirmAllProposals = () => {
    if (!reviewCase) return;
    const eventStatuses = new Map(events.map(event => [event.goldEventId, event.status]));
    const draftById = new Map(reviewCase.candidates.map(candidate => [candidate.candidateId, candidate]));
    setCandidateReviews(current => current.map(review => {
      if (review.status === 'confirmed') return review;
      const draft = draftById.get(review.candidateId);
      if (!draft) return review;
      if (draft.decision === 'event' && eventStatuses.get(draft.goldEventId || '') === 'rejected') {
        return review;
      }
      return {
        ...review,
        status: 'confirmed',
        decision: draft.decision,
        goldEventId: draft.decision === 'event' ? draft.goldEventId : null,
      };
    }));
    setDirty(true);
  };

  const finalize = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveExtractionBatchReviewer(reviewer);
      const response = await finalizeExtractionBatchReview();
      setFinalResult(response);
      setSummary(response.summary);
      setFinalizeOpen(false);
    } catch (finalizeError: any) {
      setError(finalizeError?.message || 'Could not finalize the human review');
      setFinalizeOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const visibleEvents = events.map((event, index) => ({ event, index }))
    .filter(({ event }) => !pendingOnly || event.status === 'pending');
  const visibleCandidates = (reviewCase?.candidates || []).map((candidate, index) => ({
    candidate,
    review: candidateReviews[index],
    index,
  })).filter(({ review }) => review && (!pendingOnly || review.status === 'pending'));
  const overallPercentage = percentage(summary?.reviewed || 0, summary?.total || 0);
  const activeIndex = summary?.cases.findIndex(item => item.caseId === reviewCase?.caseId) ?? -1;

  return (
    <AdminLayout
      currentPage="batchVerification"
      pageTitle="Verify Batch Extraction Quality"
      pageDescription="Compare the frozen source with source-derived events and model predictions, then save an independent human review."
    >
      <Banner tone="warning">
        <strong>Local verification workspace.</strong> This screen writes ignored evaluation artifacts on this machine only.
        It is not backed by a production API and cannot change events or enable Batch routing.
      </Banner>
      {error && <Banner tone="error">{error}</Banner>}
      {finalResult && (
        <Banner tone={finalResult.result.score.gate.passes ? 'success' : 'warning'}>
          Review finalized. Quality gate: <strong>{finalResult.result.score.gate.passes ? 'passed' : 'not passed'}</strong>.
          {' '}Labels: {finalResult.result.labelsPath}. Score: {finalResult.result.scorePath}.
        </Banner>
      )}

      <PageHeader>
        <div>
          <ProgressTrack aria-label="Overall verification progress">
            <ProgressFill percentage={overallPercentage} />
          </ProgressTrack>
          <Meta>{summary ? `${summary.reviewed} of ${summary.total} decisions reviewed · ${summary.remaining} remaining` : 'Loading review state…'}</Meta>
        </div>
        <ButtonRow>
          <ReviewerField>Reviewer name
            <Input aria-label="Reviewer name" value={reviewer} maxLength={200}
              onChange={change => setReviewer(change.target.value)} placeholder="Your name" />
          </ReviewerField>
          <SecondaryButton onClick={saveReviewer} disabled={saving || !reviewer.trim()}>
            <Save size={16} /> Save name
          </SecondaryButton>
          <PrimaryButton onClick={() => setFinalizeOpen(true)}
            disabled={saving || dirty || !summary?.complete || !reviewer.trim()}>
            <ShieldCheck size={16} /> Finalize review
          </PrimaryButton>
        </ButtonRow>
      </PageHeader>

      <Card>
        <CardTitle>Cases</CardTitle>
        <CardDescription>Start with amber cases. Green cases are fully reviewed and saved.</CardDescription>
        <Cases>
          {(summary?.cases || []).map(item => (
            <CaseButton key={item.caseId} active={item.caseId === reviewCase?.caseId}
              complete={item.progress.complete} onClick={() => chooseCase(item.caseId)}>
              {item.progress.complete ? <FileCheck2 size={18} color={colors.success[600]} />
                : item.manifestDuplicate ? <AlertTriangle size={18} color={colors.error[600]} />
                  : <RefreshCw size={18} color={colors.warning[600]} />}
              <div>
                <CaseName title={item.entityName || item.caseId}>{item.entityName || item.caseId}</CaseName>
                <CaseProgress>{item.progress.reviewed}/{item.progress.total} · {item.caseId}</CaseProgress>
              </div>
            </CaseButton>
          ))}
        </Cases>
      </Card>

      {reviewCase && (
        <>
          {reviewCase.manifestDuplicate && (
            <Banner tone="error">
              This case ID has conflicting manifest labels: {reviewCase.manifestEntityNames.join(' / ')}.
              Review the source itself, not the entity label.
            </Banner>
          )}
          <Workspace>
            <SourcePane reviewCase={reviewCase} />
            <Pane aria-label="Verification decisions">
              <PaneHeader>
                <div>
                  <PaneTitle>{reviewCase.entityName || reviewCase.caseId}</PaneTitle>
                  <Meta>{localProgress.reviewed}/{localProgress.total} reviewed {dirty ? '· unsaved changes' : '· saved'}</Meta>
                </div>
                <Segments aria-label="Review view">
                  <Segment active={tab === 'events'} onClick={() => setTab('events')}>
                    Source events ({localProgress.eventsReviewed}/{events.length})
                  </Segment>
                  <Segment active={tab === 'predictions'} onClick={() => setTab('predictions')}>
                    Predictions ({localProgress.candidatesReviewed}/{candidateReviews.length})
                  </Segment>
                </Segments>
              </PaneHeader>
              <ReviewToolbar>
                <Toggle><input type="checkbox" checked={pendingOnly}
                  onChange={change => setPendingOnly(change.target.checked)} /> Pending only</Toggle>
                <ButtonRow>
                  {tab === 'events' ? (
                    <>
                      <SmallButton onClick={() => { setEvents(current => [...current, newEvent()]); setDirty(true); }}>
                        <Plus size={14} /> Add missed event
                      </SmallButton>
                      <SmallButton tone="success" onClick={confirmAllEvents}>
                        <CheckCheck size={14} /> Confirm all source events
                      </SmallButton>
                    </>
                  ) : (
                    <SmallButton tone="success" onClick={confirmAllProposals}>
                      <CheckCheck size={14} /> Confirm all proposed mappings
                    </SmallButton>
                  )}
                </ButtonRow>
              </ReviewToolbar>
              <ReviewBody>
                {tab === 'events' && visibleEvents.map(({ event, index }) => (
                  <EventReview key={event.goldEventId} event={event}
                    onChange={value => value.status === 'rejected'
                      ? rejectEvent(index, value) : updateEvent(index, value)}
                    onDelete={() => { setEvents(current => current.filter((_, i) => i !== index)); setDirty(true); }} />
                ))}
                {tab === 'predictions' && visibleCandidates.map(({ candidate, review, index }) => (
                  <CandidateReview key={candidate.candidateId} candidate={candidate} review={review}
                    events={events} onChange={value => updateCandidate(index, value)} />
                ))}
                {((tab === 'events' && visibleEvents.length === 0)
                  || (tab === 'predictions' && visibleCandidates.length === 0)) && (
                  <Empty>{pendingOnly ? 'No pending decisions in this view.' : 'No decisions in this view.'}</Empty>
                )}
              </ReviewBody>
            </Pane>
          </Workspace>

          <Card style={{ marginTop: spacing[4] }}>
            <ButtonRow style={{ justifyContent: 'space-between' }}>
              <SecondaryButton disabled={activeIndex <= 0 || dirty}
                onClick={() => summary && loadCase(summary.cases[activeIndex - 1].caseId)}>
                <ChevronLeft size={16} /> Previous case
              </SecondaryButton>
              <ButtonRow>
                <SecondaryButton onClick={() => saveCurrent(false)} disabled={!dirty || saving}>
                  <Save size={16} /> Save case
                </SecondaryButton>
                <PrimaryButton onClick={() => saveCurrent(true)} disabled={saving || !localProgress.complete}>
                  Save and next <ChevronRight size={16} />
                </PrimaryButton>
              </ButtonRow>
              <SecondaryButton disabled={!summary || activeIndex < 0
                || activeIndex >= summary.cases.length - 1 || dirty}
                onClick={() => summary && loadCase(summary.cases[activeIndex + 1].caseId)}>
                Next case <ChevronRight size={16} />
              </SecondaryButton>
            </ButtonRow>
          </Card>
        </>
      )}

      {!reviewCase && !loading && !error && <Banner tone="info">No review cases are available.</Banner>}

      <ConfirmDialog
        open={finalizeOpen}
        title="Finalize independent human review"
        body={(
          <div>
            This locks the current decisions into a separate human-reviewed label file and runs the quality gate.
            It does not enable Batch or change production events.
          </div>
        )}
        confirmPhrase="FINALIZE REVIEW"
        primaryLabel="Finalize and score"
        isProcessing={saving}
        onConfirm={finalize}
        onCancel={() => setFinalizeOpen(false)}
      />
    </AdminLayout>
  );
}